import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

const navItems = (t) => [
  { to: "/", label: t.nav.home },
  { to: "/aanbod", label: t.nav.properties },
  { to: "/diensten", label: t.nav.services },
  { to: "/wie-we-zijn", label: t.nav.about },
  { to: "/journaal", label: t.nav.blog },
  { to: "/contact", label: t.nav.contact },
];

export default function Header() {
  const { lang, setLang, t, langs } = useLang();
  const [open, setOpen] = useState(false);
  const items = navItems(t);

  return (
    <header className="glass-nav glass-nav-scrolled fixed top-0 inset-x-0 z-50" data-testid="site-header">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 pt-4 sm:pt-2.5 pb-2.5 flex items-center justify-between gap-4">
        {/* Brand lockup: transparent NM logo + underlined brand name */}
        <Link
          to="/"
          className="flex items-center gap-3 md:gap-4 group"
          data-testid="logo-link"
          aria-label="NM Luxury Estates — Huis aan de Costa del Sol"
        >
          <img
            src="/nm-logo-gold.png"
            alt="NM Luxury Estates"
            data-testid="nm-logo"
            className="select-none"
            style={{
              height: "36px",
              width: "auto",
              background: "transparent",
              display: "block",
            }}
          />
          <span
            className="brand-underline font-serif text-base md:text-lg lg:text-xl tracking-tight text-cream hidden sm:inline-block"
            data-testid="brand-name"
          >
            Huis aan de Costa del Sol
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) =>
                `text-[0.74rem] tracking-[0.22em] uppercase font-light transition-colors duration-200 ${
                  isActive ? "text-gold" : "text-cream hover:text-gold"
                }`
              }
              data-testid={`nav-${it.to.replace(/\//g, "") || "home"}`}
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        {/* Right: single vertical language toggle (desktop) + mobile burger */}
        <div className="flex items-center gap-3">
          {/* Single inline horizontal language toggle — compact, contained */}
          <div
            className="hidden md:flex items-stretch border border-cream/15 divide-x divide-cream/10"
            data-testid="lang-toggle"
          >
            {langs.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 text-[0.62rem] tracking-[0.22em] uppercase transition-colors leading-tight ${
                  lang === l.code ? "text-gold bg-gold/10" : "text-cream/55 hover:text-cream"
                }`}
                data-testid={`lang-${l.code}`}
                aria-label={`Switch language to ${l.label}`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            className="lg:hidden p-2 text-cream"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-cream/10 bg-charcoal-surface" data-testid="mobile-menu">
          <div className="max-w-[1480px] mx-auto px-6 py-6 flex flex-col gap-5">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-sm tracking-[0.22em] uppercase font-light ${
                    isActive ? "text-gold" : "text-cream"
                  }`
                }
                data-testid={`mobile-nav-${it.to.replace(/\//g, "") || "home"}`}
              >
                {it.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 mt-4">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1 text-[0.65rem] tracking-[0.22em] uppercase border ${
                    lang === l.code
                      ? "border-gold text-gold"
                      : "border-cream/15 text-cream/65"
                  }`}
                  data-testid={`mobile-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
